import { useDeleteChatMessageMutation, useChatBanUserMutation } from '@http';
import { HoverItem, HoverMenu } from '@components/helpers/HoverMenu';
import { PlayerModal } from '@components/chat/PlayerModal';
import { ChatBadge } from '@components/chat/ChatBadge';
import { useQueryClient } from '@tanstack/react-query';
import { useLobbyStore, useAppStore } from '@stores';
import { CombinedChatMessage } from '@helpers/chat';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

interface MessageProps {
  message: CombinedChatMessage;
}

export function Message({ message }: MessageProps) {
  const [renderCount, setRenderCount] = useState(0);

  const { t } = useTranslation(undefined, { keyPrefix: 'Chat' });
  const { mutateAsync: deleteAsync } = useDeleteChatMessageMutation();
  const { mutateAsync: banAsync } = useChatBanUserMutation();
  const { state: lobbyState } = useLobbyStore();
  const queryClient = useQueryClient();
  const { userData } = useAppStore();

  const timeAgo = () => {
    const seconds = Math.floor((Date.now() - new Date(message.createdAt).getTime()) / 1000);
    if (seconds < 10) return t('Format.justNow');
    if (seconds < 60) return t('Format.secondsAgo', { seconds });
    if (seconds < 3600) return t('Format.minutesAgo', { minutes: Math.floor(seconds / 60) });
    if (seconds < 86400) return t('Format.hoursAgo', { hours: Math.floor(seconds / 3600) });
    return t('Format.daysAgo', { days: Math.floor(seconds / 86400) });
  };

  const deleteMessage = async () => {
    await deleteAsync({ id: message.id });
    queryClient.refetchQueries({ queryKey: ['chat'] });
  };

  const banUser = async () => {
    await banAsync({ id: message.author?.id ?? '' });
    queryClient.refetchQueries({ queryKey: ['chat'] });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setRenderCount(renderCount + 1);
    }, 1000);
    return () => clearInterval(interval);
  });

  if (userData.blocked.includes(message.author?.id ?? '')) return null;

  const isModerator = ['MODERATOR', 'ADMIN'].includes(userData.role);
  const hoverItems: HoverItem[] = [
    { label: t('delete'), className: 'btn-danger', onClick: deleteMessage },
    { label: t('ban'), className: 'btn-danger', onClick: banUser },
  ];

  const isPremium = message.author?.premiumUntil
    && new Date(message.author.premiumUntil) >= new Date();

  return (
    <div className={`chat-message chat-${message.type} role-${message.author?.role ?? 'SYSTEM'} ${isPremium ? 'premium' : ''}`}>
      <div className="flex">
        <div className="chat-header">
          {message.author && (
            <PlayerModal disabled={!!lobbyState?.gameId} id={message.author.id}>
              <button>
                {message.author.username}{' '}
              </button>
            </PlayerModal>
          )}
          {!message.author && <p>{t('system')}</p>}
          <ChatBadge message={message} />
        </div>

        <div className="flex items-center justify-between w-full">
          <p className="chat-time">{timeAgo()}</p>

          {message.author?.avatar && (
            <div className="img-container">
              <img src={`/img/avatars/${message.author.avatar}.avif`} alt="avatar" />
            </div>
          )}
        </div>
      </div>
      <HoverMenu disabled={!isModerator} items={hoverItems} horizontal>
        <div className="chat-body">
          {message.type === 'LEFT_CHAT' && (
            <p className="text-gray-500">
              {t('leftChat', { username: message.author?.username })}
            </p>
          )}

          {message.type !== 'LEFT_CHAT' && message.content.map((c, i) => (
            <p className="message-content" key={i}>{c}</p>
          ))}
        </div>
      </HoverMenu>
    </div>
  );
}
